import { lerDaApi as ler } from './giro';

/**
 * VIDA DO PRODUTO (dono, 23/10/2026) — o front de /api/vida-do-produto. Os tipos espelham
 * @inovapay/domain (vida-do-produto) e @inovapay/database (vida-do-produto).
 */

export type CategoriaDaVida =
  'ENTRADA' | 'CHEGADA' | 'VENDA' | 'AJUSTE' | 'GIRO' | 'DIVERGENCIA' | 'IGOR' | 'CADASTRO';
export type TomDoSelo = 'ok' | 'alerta' | 'erro' | 'neutro';

export interface EventoDaVida {
  readonly chave: string;
  readonly categoria: CategoriaDaVida;
  readonly em: string | null;
  readonly dia: string;
  readonly titulo: string;
  readonly detalhe: string | null;
  readonly qtd: number | null;
  readonly saldo: number | null;
  readonly saldoAproximado: boolean;
  readonly quem: { readonly nome: string; readonly papel: string } | null;
  readonly documento: string | null;
  readonly fonte: 'HOS' | 'INOVAPAY' | 'GIRO' | 'ENCOMENDAS';
  readonly numeros: readonly {
    readonly rotulo: string;
    readonly valor: string;
    readonly tom?: TomDoSelo;
  }[];
  readonly selo: { readonly texto: string; readonly tom: TomDoSelo } | null;
  readonly cancelado: boolean;
}

export interface ResumoDaVida {
  readonly entrou: number;
  readonly vendeu: number;
  readonly voltou: number;
  readonly ajustes: number;
  readonly perdas: number;
  readonly chegouNaLoja: number;
  readonly contagens: number;
  readonly reposto: number;
  readonly divergencias: number;
  readonly igor: { readonly tudo: number; readonly parte: number; readonly nada: number };
  readonly saldoInicio: number | null;
  readonly saldoFim: number | null;
  readonly vendedores: readonly { readonly nome: string; readonly qtd: number }[];
}

export interface CabecalhoDaVida {
  readonly id: string;
  readonly descricao: string;
  readonly fabricante: string | null;
  readonly grupo: string | null;
  readonly classificacao: string | null;
  readonly principioAtivo: string | null;
  readonly codigoHos: string | null;
  readonly ean: string | null;
  readonly foto: string | null;
  readonly localizacao: string | null;
  readonly estoqueCadastro: number | null;
  readonly preco: string | null;
  readonly pmc: string | null;
  readonly ultimaVenda: string | null;
  readonly ultimaCompra: string | null;
  readonly setores: readonly string[];
}

export interface PassoDoCaso {
  readonly em: string;
  readonly texto: string;
  readonly quem: string | null;
  readonly tom: TomDoSelo;
}

/** A conta do caso: prateleira + estoque contado pelo Iggor contra o HOS na hora. */
export interface ContaDoCaso {
  readonly prateleira: number;
  readonly estoque: number | null;
  readonly estoqueMaisDe: boolean;
  readonly achado: number | null;
  readonly hos: number | null;
  readonly hosEm: string;
  /** > 0 falta, < 0 sobra; null = não dá para fechar a conta. */
  readonly diferenca: number | null;
}

/** Uma divergência do produto, do começo ao fim. */
export interface CasoDaVida {
  readonly numero: number;
  readonly documento: string;
  readonly titulo: string;
  readonly abertaEm: string;
  readonly balconista: string | null;
  readonly selo: { readonly texto: string; readonly tom: TomDoSelo };
  readonly etapa: string | null;
  readonly passos: readonly PassoDoCaso[];
  readonly conta: ContaDoCaso | null;
  readonly aviso: string | null;
}

export interface VidaDoProduto {
  readonly de: string;
  readonly ate: string;
  readonly produto: CabecalhoDaVida;
  readonly saldoAgora: { readonly saldo: number; readonly em: string } | null;
  readonly emConferencia: readonly {
    readonly numero: number | null;
    readonly unidades: number;
    readonly entrada: string | null;
  }[];
  readonly eventos: readonly EventoDaVida[];
  /** As divergências do período e as ainda abertas, a mais nova primeiro. */
  readonly casos: readonly CasoDaVida[];
  readonly resumo: ResumoDaVida;
  readonly fonteDoEstoque: 'HOS_RAZAO' | 'INOVAPAY_VENDAS';
  readonly avisos: readonly string[];
}

export interface LinhaDaVida {
  readonly id: string;
  readonly descricao: string;
  readonly fabricante: string | null;
  readonly grupo: string | null;
  readonly classificacao: string | null;
  readonly codigoHos: string | null;
  readonly ean: string | null;
  readonly foto: string | null;
  readonly estoque: number | null;
  readonly preco: string | null;
  readonly vendido: number;
  readonly cupons: number;
  readonly contagens: number;
  readonly reposto: number;
  readonly divergencias: number;
  readonly igorAchou: number;
  readonly igorParte: number;
  readonly igorNaoAchou: number;
  readonly chegou: number;
  readonly ultimoFato: string | null;
}

export interface ListaDaVida {
  readonly itens: readonly LinhaDaVida[];
  readonly total: number;
  readonly de: string;
  readonly ate: string;
  readonly pagina: number;
  readonly tamanhoDaPagina: number;
}

export interface OpcoesDaVida {
  readonly grupos: readonly string[];
  readonly classificacoes: readonly string[];
  readonly fabricantes: readonly string[];
  readonly setores: readonly { readonly id: string; readonly nome: string }[];
  readonly pessoas: readonly { readonly id: string; readonly nome: string }[];
}

export type FiltroDoGiro =
  | ''
  | 'com_divergencia'
  | 'sem_divergencia'
  | 'igor_achou'
  | 'igor_parte'
  | 'igor_nao_achou'
  | 'contado';

export type OrdemDaLista =
  | 'recentes'
  | 'mais_vendidos'
  | 'mais_divergencias'
  | 'mais_contados'
  | 'mais_chegou'
  | 'descricao';

export interface FiltrosDaLista {
  readonly de: string;
  readonly ate: string;
  readonly busca: string;
  readonly grupo: string;
  readonly classificacao: string;
  readonly fabricante: string;
  readonly setor: string;
  readonly pessoa: string;
  readonly giro: FiltroDoGiro;
  readonly todos: boolean;
  readonly ordem: OrdemDaLista;
  readonly pagina: number;
}

export function consultaDaLista(f: FiltrosDaLista): string {
  const q = new URLSearchParams({ de: f.de, ate: f.ate });
  const extras: [string, string][] = [
    ['busca', f.busca.trim()],
    ['grupo', f.grupo],
    ['classificacao', f.classificacao],
    ['fabricante', f.fabricante],
    ['setor', f.setor],
    ['pessoa', f.pessoa],
    ['giro', f.giro],
    ['todos', f.todos ? '1' : ''],
    ['ordem', f.ordem === 'recentes' ? '' : f.ordem],
    ['pagina', f.pagina > 1 ? String(f.pagina) : ''],
  ];
  for (const [k, v] of extras) if (v) q.set(k, v);
  return q.toString();
}

export const lerOpcoesDaVida = () => ler<OpcoesDaVida>('/api/vida-do-produto/opcoes');
export const lerListaDaVida = (f: FiltrosDaLista) =>
  ler<ListaDaVida>(`/api/vida-do-produto?${consultaDaLista(f)}`);
export const lerVidaDoProduto = (id: string, de: string, ate: string) =>
  ler<VidaDoProduto>(`/api/vida-do-produto/${encodeURIComponent(id)}?de=${de}&ate=${ate}`);

/* ------------------------------------------------------------------ apresentação (testável) */

export const NOME_DA_CATEGORIA: Readonly<Record<CategoriaDaVida, string>> = {
  ENTRADA: 'Entrada no HOS',
  CHEGADA: 'Chegada na loja',
  VENDA: 'Vendas',
  AJUSTE: 'Ajuste e perda',
  GIRO: 'Giro',
  DIVERGENCIA: 'Divergência',
  IGOR: 'Conferência do estoque',
  CADASTRO: 'Cadastro',
};

/** Os eventos agrupados por dia (a ordem do servidor: mais novo primeiro). */
export function porDia(
  eventos: readonly EventoDaVida[],
): { dia: string; eventos: EventoDaVida[] }[] {
  const grupos: { dia: string; eventos: EventoDaVida[] }[] = [];
  for (const e of eventos) {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.dia === e.dia) ultimo.eventos.push(e);
    else grupos.push({ dia: e.dia, eventos: [e] });
  }
  return grupos;
}

/** O filtro da linha do tempo: categorias, pessoa e o texto (título, detalhe, documento). */
export function filtrarEventos(
  eventos: readonly EventoDaVida[],
  f: { categorias: ReadonlySet<CategoriaDaVida>; pessoa: string; texto: string },
): EventoDaVida[] {
  const t = f.texto.trim().toLocaleLowerCase('pt-BR');
  return eventos.filter(
    (e) =>
      (f.categorias.size === 0 || f.categorias.has(e.categoria)) &&
      (!f.pessoa || e.quem?.nome === f.pessoa) &&
      (!t ||
        [e.titulo, e.detalhe, e.documento, e.quem?.nome]
          .filter(Boolean)
          .some((s) => (s as string).toLocaleLowerCase('pt-BR').includes(t))),
  );
}

/**
 * A curva do saldo no período, do mais velho ao mais novo: um ponto por movimento que tem saldo.
 * Começa no saldo do início e termina no do fim (quando o servidor deu os dois).
 */
export function curvaDoSaldo(
  eventos: readonly EventoDaVida[],
  resumo: ResumoDaVida,
): { quando: number; saldo: number; chave: string | null }[] {
  const momento = (e: EventoDaVida) => Date.parse(e.em ?? `${e.dia}T23:59:59.999-03:00`);
  const pontos = eventos
    .filter((e) => e.qtd !== null && e.saldo !== null && !e.cancelado)
    .map((e) => ({ quando: momento(e), saldo: e.saldo as number, chave: e.chave }))
    .sort((a, b) => a.quando - b.quando);
  const primeiro = pontos[0];
  return primeiro && resumo.saldoInicio !== null
    ? [{ quando: primeiro.quando - 1, saldo: resumo.saldoInicio, chave: null }, ...pontos]
    : pontos;
}

/**
 * A conta do caso numa frase: "HOS 4 · achado 2 (prateleira 2 + estoque 0) · faltam 2".
 * Sem a contagem do estoque, diz o que falta; sem HOS, não inventa a diferença.
 */
export function fraseDaConta(c: ContaDoCaso): { texto: string; tom: TomDoSelo } {
  const hos = c.hos !== null ? `HOS ${unidades(c.hos)}` : 'HOS sem leitura';
  if (c.estoque === null)
    return {
      texto: `${hos} · prateleira ${unidades(c.prateleira)} · falta contar o estoque`,
      tom: 'neutro',
    };
  const estoque = c.estoqueMaisDe ? `mais de ${unidades(c.estoque)}` : unidades(c.estoque);
  const achado = `achado ${c.estoqueMaisDe ? 'mais de ' : ''}${unidades(c.achado ?? 0)} (prateleira ${unidades(c.prateleira)} + estoque ${estoque})`;
  if (c.diferenca === null) return { texto: `${hos} · ${achado}`, tom: 'neutro' };
  if (c.diferenca > 0)
    return {
      texto: `${hos} · ${achado} · falta${c.diferenca === 1 ? '' : 'm'} ${unidades(c.diferenca)}`,
      tom: 'erro',
    };
  if (c.diferenca < 0)
    return {
      texto: `${hos} · ${achado} · sobra${c.diferenca === -1 ? '' : 'm'} ${unidades(-c.diferenca)}`,
      tom: 'alerta',
    };
  return { texto: `${hos} · ${achado} · bate`, tom: 'ok' };
}

/** "+3 un", "−2 un", "0 un" — o sinal de verdade (U+2212), número em pt-BR. */
export function unidades(n: number, sinal = false): string {
  const a = Math.abs(n);
  const t = Number.isInteger(a)
    ? a.toLocaleString('pt-BR')
    : a.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
  const s = sinal ? (n > 0 ? '+' : n < 0 ? '−' : '') : n < 0 ? '−' : '';
  return `${s}${t}`;
}
